/**
 * Prepares a picked photo for upload (FR-17).
 *
 * Every image is decoded and re-encoded as a JPEG no larger than MAX_EDGE on
 * its long side. Three reasons, in order of importance:
 *
 *   1. It strips EXIF. A phone photo carries GPS coordinates; a consultation
 *      photo of a child's lab result must not carry where they live.
 *   2. Size. A 12-megapixel photo is 3–6 MB over a family's mobile data plan;
 *      2000 px is plenty to read a lab sheet and lands around 300–600 KB.
 *   3. One format. The bucket accepts JPEG only, so HEIC from an iPhone or a
 *      PNG screenshot all arrive the same way.
 */

export const MAX_EDGE = 2000;
export const JPEG_QUALITY = 0.85;
/** Refuse absurd inputs before decoding them into memory. */
export const MAX_INPUT_BYTES = 25 * 1024 * 1024;

/** The long-side-limited size for an image, never upscaled. */
export function fitWithin(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) {
    return { width, height };
  }
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export async function prepareConsultationImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new Error('File harus berupa foto.');
  }
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Foto terlalu besar (maksimal 25 MB sebelum diperkecil).');
  }

  let bitmap: ImageBitmap;
  try {
    // imageOrientation: 'from-image' applies the EXIF rotation before the
    // metadata is discarded, so a portrait photo does not come out sideways.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new Error('Foto tidak bisa dibaca. Coba foto lain atau format JPG.');
  }

  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) {
    bitmap.close();
    throw new Error('Perangkat tidak bisa memproses foto.');
  }
  // White under any transparency: JPEG has no alpha, and a transparent PNG
  // would otherwise turn black.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>(resolve =>
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
  );
  if (!blob) {
    throw new Error('Gagal memproses foto.');
  }
  return blob;
}
